import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Grid, TextField, Button, Paper, useMediaQuery, Snackbar, Divider } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import apiService from '../components/ReasuableFuncations/api';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const ICTNewsEvents = () => {
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [newsInput, setNewsInput] = useState('');
  const [eventInput, setEventInput] = useState('');
  const [eventDate, setEventDate] = useState(new Date());
  const [contentInput, setContentInput] = useState('');
  const [eventContentInput, setEventContentInput] = useState('');
  // const [filteredEvents, setFilteredEvents] = useState([]);
  const [newsImage, setNewsImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false)
  
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' // Can be 'success' or 'error'
  });
  const [loadingNews, setLoadingNews] = useState(false); // Loader state for news
  const [loadingEvent, setLoadingEvent] = useState(false); // Loader state for event

  const userData = JSON.parse(localStorage.getItem('user'));

  const { selectedOption, regionalNetworkOption, memberStateOption, countryOption } = userData;


  // Helper function to show snackbar
  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      let newsUrl = `/newsByUser`;
      let eventsUrl = `/eventsByUser`;
      const newsResponse = await apiService.get(newsUrl);
      const eventsResponse = await apiService.get(eventsUrl);
      if(newsResponse.statusText !== 'OK' || eventsResponse.statusText !== 'OK'){
        throw new Error('Error fetching data');
      }
      setNews(newsResponse.data);
      setEvents(eventsResponse.data);
      // setFilteredEvents(eventsResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Error fetching data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userData) {
      console.error('User data not found. Redirecting to login.');
      return;
    }
    fetchData();
  }, []);

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setNewsImage(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    } else {
      showSnackbar('Please upload a valid image file', 'error');
    }
  };

  const handlePostNews = async () => {
    if (newsInput && contentInput && newsImage) {
      setLoadingNews(true);
      const formData = new FormData();
      formData.append('title', newsInput);
      formData.append('content', contentInput);
      formData.append('image', newsImage);
      formData.append('selectedOption', selectedOption);
      formData.append('regionalNetworkOption', regionalNetworkOption);
      formData.append('memberStateOption', memberStateOption);
      formData.append('countryOption', countryOption);

      try {
        const response = await apiService.post(`/news`, formData);
        if(response.statusText !== 'Created'){
          throw new Error('Error posting news');
        }
        showSnackbar('News posted successfully!', 'success');
        setNewsInput('');
        setContentInput('');
        setNewsImage(null);
        setImagePreview(null);
        fetchData(); // Refresh the news list
      } catch (error) {
        console.error('Error posting news:', error);
        showSnackbar('Error posting news!', 'error');
      } finally {
        setLoadingNews(false);
      }
    } else {
      showSnackbar('Please enter a news title, content, and upload an image', 'error');
    }
  };

  const handlePostEvent = async () => {
    if (eventInput && eventContentInput) {
      setLoadingEvent(true);
      try {
        const response = await apiService.post(`/events`, {
          title: eventInput,
          content: eventContentInput,
          date: eventDate.toISOString(),
          selectedOption,
          regionalNetworkOption,
          memberStateOption,
          countryOption,
        });
        if(response.statusText !== 'Created'){
          throw new Error('Error posting event');
        }
        showSnackbar('Event posted successfully!', 'success');
        setEventInput('');
        setEventContentInput('');
        setEventDate(new Date());
        fetchData(); // Refresh the events list
      } catch (error) {
        console.error('Error posting event:', error);
        showSnackbar('Error posting event!', 'error');
      } finally {
        setLoadingEvent(false);
      }
    } else {
      showSnackbar('Please enter an event title and content', 'error');
    }
  };

  const handleDeleteNews = async (id) => {
    try {
      const response  =  await apiService.delete(`/news/${id}`);
      if(response.statusText !== 'OK'){
        throw new Error('Error deleting news');
      }
      setNews(news.filter((n) => n._id !== id));
      showSnackbar('News deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting news:', error);
      showSnackbar('Error deleting news!', 'error');
    }
  };

  const handleDeleteEvent = async (id) => {
    try {
       const response = await apiService.delete(`/events/${id}`);
      if(response.statusText !== 'OK'){
        throw new Error('Error deleting event');
      }
      setEvents(events.filter((e) => e._id !== id));
      showSnackbar('Event deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting event:', error);
      showSnackbar('Error deleting event!', 'error');
    }
  };

  

  const isMobile = useMediaQuery('(max-width:600px)');

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  return (
    <Container p={2}>
      <Grid container spacing={2}>
        {/* Left Side: News Posting Form */}
        <Grid item xs={12} sm={6}>
          <Paper elevation={20} sx={{ p: 4, mb: 4, height: '100%', minHeight: '300px' }}>
            <Typography variant="h5" gutterBottom align="center">
              Post ICT News
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="News Title"
                  value={newsInput}
                  onChange={(e) => setNewsInput(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Content"
                  value={contentInput}
                  multiline
                  rows={2}
                  maxRows={4}
                  onChange={(e) => setContentInput(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ marginBottom: '10px' }}
                />
              </Grid>
              
              {imagePreview && (
                <Grid item xs={12}>
                  <Box sx={{ 
                    mb: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    height: '200px',
                    overflow: 'hidden',
                    borderRadius: 1,
                    border: '1px solid #ccc'
                  }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <Button 
                  fullWidth 
                  variant="contained" 
                  onClick={handlePostNews} 
                  disabled={loadingNews} // Disable button during loading
                >
                  {loadingNews ? 'Posting News...' : 'Post News'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Right Side: Event Posting Form */}
        <Grid item xs={12} sm={6}>
          <Paper elevation={20} sx={{ p: 4, mb: 4, height: '100%', minHeight: '300px' }}>
            <Typography variant="h5" gutterBottom align="center">
              Post ICT Event
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Event Title"
                  value={eventInput}
                  onChange={(e) => setEventInput(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Content"
                  value={eventContentInput}
                  onChange={(e) => setEventContentInput(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <DatePicker selected={eventDate} onChange={(date) => setEventDate(date)} />
              </Grid>
              <Grid item xs={12}>
                <Button 
                  fullWidth 
                  variant="contained" 
                  onClick={handlePostEvent} 
                  disabled={loadingEvent} // Disable button during loading
                >
                  {loadingEvent ? 'Posting Event...' : 'Post Event'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* News Display */}
        <Grid item xs={12} sm={6}>
          <Paper elevation={20} sx={{ p: 4, mb: 4, height: '100%', minHeight: '300px' }}>
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 2 }}>
              Handle ICT News
            </Typography>
            {news.length === 0 ? (
              <Typography variant="body1" align="center">No news available</Typography>
            ) : (
              news.map((item) => (
                <Paper key={item._id} elevation={3} sx={{ p: 2, mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6">{item.title}</Typography>
                      <Typography variant="body2">{new Date(item.date).toLocaleDateString()}</Typography>
                    </Box>
                    <IconButton aria-label="delete" onClick={() => handleDeleteNews(item._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              ))
            )}
          </Paper>
        </Grid>

        {/* Events Display */}
        <Grid item xs={12} sm={6}>
          <Paper elevation={20} sx={{ p: 4, mb: 4, height: '100%', minHeight: '300px' }}>
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 2 }}>
              Handle ICT Events
            </Typography>
            {events.length === 0 ? (
              <Typography variant="body1" align="center">No events available</Typography>
            ) : (
              events.map((item) => (
                <Paper key={item._id} elevation={3} sx={{ p: 2, mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6">{item.title}</Typography>
                      <Typography variant="body2">{new Date(item.date).toLocaleDateString()}</Typography>
                    </Box>
                    <IconButton aria-label="delete" onClick={() => handleDeleteEvent(item._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ICTNewsEvents;